import { StandardTextFieldProps, TextField } from '@mui/material';
import { ChangeEvent, ReactElement, useEffect, useState } from 'react';

export interface DebouncedTextFieldProps
  extends Omit<StandardTextFieldProps, 'onChange'> {
  /**
   * @desc Called after input stops changing for a number of milliseconds
   * @param value from event:ChangeEvent<HTMLInputElement> target
   * @param name from event:ChangeEvent<HTMLInputElement> target
   */
  onChange: (value: string, name: string) => void;
  /** @description time delay in milliseconds @optional @defaultValue 400 milliseconds */
  timeout?: number;
  /** @description value to be shown in the text field */
  value?: string;
}

/**
 * @desc Causes the onChange function to be delayed by milliseconds after the input stops changing.
 * This has the effect of not causing re-renders on each key-up event when the user types multiple
 * characters.
 * @param props
 * @returns An MUI TextField component wrapped in a time-delay
 */
export const DebouncedTextField: React.FC<DebouncedTextFieldProps> = (
  props: DebouncedTextFieldProps,
): ReactElement => {
  const [fieldTimeout, setFieldTimeout] = useState<NodeJS.Timeout | null>(null);
  const [internalValue, setInternalValue] = useState(props.value || '');

  // Sync internal value with external value prop changes
  useEffect(() => {
    if (props.value !== undefined) {
      setInternalValue(props.value);
    }
  }, [props.value]);

  /**
   * @desc manages timeouts to delay the props.onChange function until input stops changing
   * @param event the change event from the MUI TextField component
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value, name } = event.target;

    // Update internal value immediately for responsive UI
    setInternalValue(value);

    // if there is a delay pending already
    if (fieldTimeout) {
      // clear the existing timeout
      clearTimeout(fieldTimeout);
    }
    // call props.onChange after a delay
    const ref: NodeJS.Timeout = setTimeout(() => {
      props.onChange(value, name);
      // reset to null so that next onChange doesn't try to clearout an already cleared out reference
      setFieldTimeout(null);
    }, props.timeout || 400);
    // store the timeout reference to potentially clear before it runs out
    setFieldTimeout(ref);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fieldTimeout) {
        clearTimeout(fieldTimeout);
      }
    };
  }, [fieldTimeout]);

  return (
    <TextField
      {...(props as any)}
      value={internalValue}
      onChange={handleChange}
    />
  );
};
